import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, Plus, Minus, ArrowLeft } from "lucide-react";
import { getProduct, requestStockNotification } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useLang } from "@/context/LanguageContext";
import { pickL } from "@/lib/content";
import Breadcrumb from "@/components/Breadcrumb";
import ProductCard from "@/components/ProductCard";
import useReveal from "@/hooks/useReveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const settings = useSettings();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState("idle");
  const [notifyMsg, setNotifyMsg] = useState("");
  useReveal([data]);

  useEffect(() => {
    setData(null);
    setError(false);
    setActiveImg(0);
    setQty(1);
    getProduct(slug).then(setData).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="empty-state" data-testid="pdp-not-found">
        <h2>Produit introuvable</h2>
        <p className="section-body">Ce produit n'existe pas ou n'est plus disponible.</p>
        <Link to="/collections/all" className="btn-primary">Retour à la boutique</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="loader"><div className="spinner" /></div>;
  }

  const { product, related } = data;
  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const outOfStock = typeof product.stock === "number" && product.stock <= 0;

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const buyNow = () => {
    addItem(product, qty);
    navigate("/panier");
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail || notifyStatus === "sending") return;
    setNotifyStatus("sending");
    setNotifyMsg("");
    try {
      const res = await requestStockNotification({ email: notifyEmail, product_id: product.id });
      setNotifyStatus("done");
      setNotifyMsg(res.message || "");
      setNotifyEmail("");
    } catch (err) {
      setNotifyStatus("error");
      setNotifyMsg(err?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    }
  };

  return (
    <>
      <Breadcrumb
        trail={[
          { label: t("nav.home"), to: "/" },
          { label: t("nav.shop"), to: "/collections/all" },
          { label: product.name },
        ]}
      />

      <div className="pdp" data-testid="product-detail">
        <div>
          <div className="pdp-gallery-main">
            <img src={gallery[activeImg]} alt={`${product.name} — vue ${activeImg + 1}`} />
          </div>
          {gallery.length > 1 ? (
            <div className="pdp-thumbs">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  className={activeImg === i ? "active" : ""}
                  onClick={() => setActiveImg(i)}
                  aria-label={`Voir image ${i + 1}`}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={g} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div>
          {product.badge ? <p className="section-tag">{product.badge}</p> : null}
          <h1>{product.name}</h1>
          <p className="pdp-sub">{pickL(product, "subtitle", lang)} · {product.volume}</p>
          {product.on_sale ? (
            <p className="pdp-price">
              <span className="price-old">€{product.price.toFixed(2).replace(".", ",")}</span>
              <span className="price-sale">€{product.effective_price.toFixed(2).replace(".", ",")}</span>
            </p>
          ) : (
            <p className="pdp-price">€{product.price.toFixed(2).replace(".", ",")}</p>
          )}
          <p className="section-body" style={{ marginBottom: "1rem" }}>{pickL(product, "long_desc", lang)}</p>

          {product.story ? (
            <blockquote className="pdp-story" data-testid="pdp-story">
              <span className="pdp-story__label">{t("pdp.story")}</span>
              <p>{pickL(product, "story", lang)}</p>
            </blockquote>
          ) : null}

          <ul className="pdp-benefits">
            {(pickL(product, "benefits", lang) || []).map((b) => (
              <li key={b}><Check size={16} /> {b}</li>
            ))}
          </ul>

          {outOfStock ? (
            <div className="pdp-notify" data-testid="pdp-notify-block">
              <p className="pdp-outofstock" data-testid="pdp-out-of-stock">{t("pdp.outOfStock")}</p>
              {notifyStatus === "done" ? (
                <p className="pdp-notify__success" data-testid="pdp-notify-success">{notifyMsg}</p>
              ) : (
                <form className="pdp-notify__form" onSubmit={handleNotify} data-testid="pdp-notify-form">
                  <label className="pdp-notify__label">{t("pdp.notifyTitle")}</label>
                  <div className="pdp-notify__row">
                    <input
                      type="email"
                      required
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder={t("pdp.notifyPlaceholder")}
                      aria-label={t("pdp.notifyPlaceholder")}
                      data-testid="pdp-notify-email"
                    />
                    <button type="submit" className="btn-primary" disabled={notifyStatus === "sending"} data-testid="pdp-notify-submit">
                      {notifyStatus === "sending" ? t("pdp.notifySending") : t("pdp.notifyButton")}
                    </button>
                  </div>
                  {notifyStatus === "error" ? (
                    <p className="pdp-notify__error" data-testid="pdp-notify-error">{notifyMsg}</p>
                  ) : null}
                </form>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", margin: "1.5rem 0" }}>
                <div className="qty-control" data-testid="pdp-qty">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Diminuer"><Minus size={14} /></button>
                  <span data-testid="pdp-qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} aria-label="Augmenter"><Plus size={14} /></button>
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button className={`btn-primary ${added ? "" : ""}`} onClick={handleAdd} data-testid="pdp-add-to-cart">
                  {added ? `✓ ${t("common.added")}` : t("common.addToCart")}
                </button>
                <button className="btn-gold" onClick={buyNow} data-testid="pdp-buy-now">{t("common.buyNow")}</button>
              </div>
            </>
          )}

          <div className="pdp-accordion">
            <details open>
              <summary>{t("pdp.usage")}</summary>
              <p>{pickL(product, "usage", lang)}</p>
            </details>
            <details>
              <summary>{t("pdp.inci")}</summary>
              <p>{product.ingredients}</p>
            </details>
            <details>
              <summary>{t("pdp.shippingTitle")}</summary>
              <p>{t("pdp.shippingText").replace("{n}", String(Number(settings.free_shipping_threshold ?? 45)))}</p>
            </details>
          </div>
        </div>
      </div>

      {related && related.length ? (
        <section className="products-section reveal" style={{ paddingTop: 0 }}>
          <div className="section-header">
            <p className="section-tag">{t("pdp.relatedTag")}</p>
            <h2 className="section-title" dangerouslySetInnerHTML={{ __html: t("pdp.relatedTitle") }} />
          </div>
          <div className="products-grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
